import { Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';
import SingleQuestion from 'components-new/single-question';
import MultiQuestion from 'components-new/multi-question';
import OrderQuestion from 'components-new/ordering-question';
import MatchQuestion from 'components-new/matching-question';
import { useForm, useFieldArray } from 'react-hook-form';
import BlanksQuestion from 'components-new/blanks-question';

type AssessmentQuizProps = {
  questionPools: any;
  completed?: boolean;
  register?: any;
  control?: any;
  pageIndex: number;
  onAnswerChange?: any;
};

const AssessmentQuiz: React.FC<AssessmentQuizProps> = ({
  questionPools = [],
  completed = false,
  register,
  control,
  pageIndex,
  onAnswerChange,
}) => {
  const questionsCollectionCombined = questionPools
    .reduce((acc, { questionsCollection, sys: { id } }) => {
      const itemsWithPoolId = questionsCollection.items.map((item) => ({
        ...item,
        questionPoolId: id,
      }));
      return acc.concat(itemsWithPoolId);
    }, [])
    .filter(
      (question) =>
        question.questionFields.type === 'match' ||
        question.questionFields.type === 'single' ||
        question.questionFields.type === 'multi' ||
        question.questionFields.type === 'fill' ||
        question.questionFields.type === 'order'
    );

  const { fields: questionsFields } = useFieldArray({
    control,
    name: `pages[${pageIndex}].questions`,
  });

  const allQuestions = control._formValues.pages.flatMap((page: any, idx: number) =>
    page.questions.map((question: any, qIdx: number) => ({
      pageIndex: idx,
      questionIndex: qIdx,
    }))
  );

  return (
    <StyledQuiz>
      <div className="question-pools">
        <div className="question-pool">
          {questionsFields.map((field, qIndex: number) => {
            const question = questionsCollectionCombined[qIndex];
            let questionComponent;

            const globalQuestionNumber =
              allQuestions.findIndex((q: any) => q.pageIndex === pageIndex && q.questionIndex === qIndex) + 1;

            switch (question.questionFields.type) {
              case 'single':
                questionComponent = (
                  <SingleQuestion
                    answers={question.questionFields.answers}
                    answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                    text={question.questionFields.text}
                    question={question.content.json}
                    questionIndex={qIndex}
                    control={control}
                    completed={completed}
                    image={question.image}
                    name={`pages[${pageIndex}].questions[${qIndex}].answers`}
                    onAnswerChange={onAnswerChange}
                    questionId={question.sys.id}
                    questionPoolId={question.questionPoolId}
                    globalQuestionNumber={globalQuestionNumber}
                  />
                );
                break;
              case 'multi':
                questionComponent = (
                  <MultiQuestion
                    answers={question.questionFields.answers}
                    answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                    text={question.questionFields.text}
                    question={question.content.json}
                    questionIndex={qIndex}
                    control={control}
                    completed={completed}
                    image={question.image}
                    name={`pages[${pageIndex}].questions[${qIndex}].answers`}
                    onAnswerChange={onAnswerChange}
                    questionId={question.sys.id}
                    questionPoolId={question.questionPoolId}
                    globalQuestionNumber={globalQuestionNumber}
                  />
                );
                break;

              case 'match':
                questionComponent = (
                  <MatchQuestion
                    answers={question.questionFields.answers}
                    answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                    answersDragDrop={question.questionFields?.answersDragDrop}
                    feedback={question.questionFields.feedback}
                    question={question.content.json}
                    questionIndex={qIndex}
                    control={control}
                    completed={completed}
                    image={question.image}
                    register={register}
                    name={`pages[${pageIndex}].questions[${qIndex}].answers`}
                    onAnswerChange={onAnswerChange}
                    questionId={question.sys.id}
                    questionPoolId={question.questionPoolId}
                    globalQuestionNumber={globalQuestionNumber}
                  />
                );
                break;
              case 'order':
                questionComponent = (
                  <OrderQuestion
                    answers={question.questionFields.answers}
                    answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                    feedback={question.questionFields.feedback}
                    question={question.content.json}
                    questionIndex={qIndex}
                    control={control}
                    completed={completed}
                    image={question.image}
                    register={register}
                    name={`pages[${pageIndex}].questions[${qIndex}].answers`}
                    onAnswerChange={onAnswerChange}
                    questionId={question.sys.id}
                    questionPoolId={question.questionPoolId}
                    globalQuestionNumber={globalQuestionNumber}
                  />
                );
                break;
              case 'fill':
                questionComponent = (
                  <BlanksQuestion
                    answers={question.questionFields.answers}
                    answersCurrentLocale={question.questionFieldsCurrentLocale.answers}
                    text={question.questionFields.text}
                    question={question.content.json}
                    questionIndex={qIndex}
                    control={control}
                    completed={completed}
                    feedback={question.questionFields.feedback}
                    image={question.image}
                    name={`pages[${pageIndex}].questions[${qIndex}].answers`}
                    onAnswerChange={onAnswerChange}
                    questionId={question.sys.id}
                    questionPoolId={question.questionPoolId}
                    globalQuestionNumber={globalQuestionNumber}
                  />
                );
                break;
              default:
                questionComponent = <></>;
            }
            return <Fragment key={field.id}>{questionComponent}</Fragment>;
          })}
        </div>
      </div>
    </StyledQuiz>
  );
};

export const StyledQuiz = styled.ul`
  margin: 1rem 0;

  .question-pool {
    display: flex;
    flex-direction: column;
    row-gap: 2rem;
  }

  .buttons-container {
    display: flex;
    column-gap: 1rem;
  }

  .validation-text {
    margin-top: 0.5rem;
    color: var(--danger-base);
    font-weight: 600;
    font-size: 0.875rem;
  }
`;

export default AssessmentQuiz;
